import {
  ApolloClient,
  InMemoryCache,
  defaultDataIdFromObject,
  split,
  HttpLink,
} from '@apollo/client'
import { BatchHttpLink } from '@apollo/client/link/batch-http'
import { relayStylePagination } from '@apollo/client/utilities'
import { createNetworkStatusNotifier } from 'react-apollo-network-status'

// @ts-ignore -- @types is our own subdirectory, not part of node_modules
import { TypedTypePolicies } from '@types/apollo-helpers'
import possibleTypes from './possibleTypes.json'

const { link } = createNetworkStatusNotifier()
const { dispatcher } = link

export { dispatcher }

const typePolicies: TypedTypePolicies = {
  Query: {
    fields: {
      collections: relayStylePagination(false),
      featuredAdventures: relayStylePagination(false),
      featuredCollections: relayStylePagination(false),
      moreEscapes: relayStylePagination(['scope']),
      search: {
        merge(existing = {}, incoming = {}, { mergeObjects }) {
          return mergeObjects(existing, incoming)
        },
      },
    },
  },
  Search: {
    fields: {
      results: relayStylePagination(false),
      result: {
        /*
          We want to fetch a single result from the cache if we have it,
          e.g. when we show a marker callout on the map, we most likely already
          have all the data we need loaded from the search query in the cache
        */
        read(_, { args, toReference }) {
          const { id, type } = args
          return toReference({
            __typename: type,
            id,
          })
        },
      },
    },
  },
  Listing: {
    fields: {
      adventures: {
        merge(_, incoming = []) {
          return incoming
        },
      },
      adventuresByProximity: relayStylePagination(false),
      nearbyAdventures: relayStylePagination(['adventureType']),
    },
  },
  StoryAdventure: {
    fields: {
      nearbyAdventures: relayStylePagination(false),
    },
  },
  Location: {
    keyFields: ['lat', 'lng'],
  },
  Booking: {
    fields: {
      adventures: {
        merge(_, incoming = []) {
          return incoming
        },
      },
      suggestedAdventures: relayStylePagination(false),
    },
  },
  Market: {
    fields: {
      adventures: relayStylePagination(false),
      featuredCollections: relayStylePagination(false),
    },
  },
  Region: {
    fields: {
      adventures: relayStylePagination(false),
    },
  },
  Collection: {
    fields: {
      items: relayStylePagination(false),
    },
  },
  // MapResult can be of type 'Listing' or 'Adventure'; the default `<__typename>:<id>` identifier alone is not unique.
  MapResult: {
    keyFields: ['type', 'id'],
  },
  UserAvatar: {
    keyFields: ['original'],
  },
}

const configureApolloClient = () => {
  const client = new ApolloClient({
    link: link.concat(
      split(
        (operation) => !!operation.getContext().batching,
        new BatchHttpLink({
          batchMax: 12,
          headers: {
            accept: 'application/json',
          },
        }),
        new HttpLink({
          credentials: 'include',
          headers: {
            accept: 'application/json',
          },
        })
      )
    ),
    cache: new InMemoryCache({
      typePolicies,

      possibleTypes,

      dataIdFromObject: (object) => {
        switch (object.__typename) {
          case 'CalendarDay':
            return `${object.__typename}:${object.date}`
          default:
            return defaultDataIdFromObject(object) // fall back to default handling
        }
      },
    }),
  })

  return client
}

export default configureApolloClient
